import type { EmbedEvent } from "./api";

const DEFAULT_EVENT_TIMEZONE = "America/Chicago";

let cachedViewerTz: string | null = null;
export function viewerTimeZone(): string {
  if (cachedViewerTz === null) {
    try {
      cachedViewerTz =
        Intl.DateTimeFormat().resolvedOptions().timeZone ??
        DEFAULT_EVENT_TIMEZONE;
    } catch {
      cachedViewerTz = DEFAULT_EVENT_TIMEZONE;
    }
  }
  return cachedViewerTz;
}

export function safeTz(tz: string | null | undefined): string {
  return tz && tz.trim() ? tz : DEFAULT_EVENT_TIMEZONE;
}

export type ZoneFormatters = {
  weekdayShort: Intl.DateTimeFormat;
  monthShort: Intl.DateTimeFormat;
  dayNum: Intl.DateTimeFormat;
  time: Intl.DateTimeFormat;
  timeCompact: Intl.DateTimeFormat;
  dayShort: Intl.DateTimeFormat;
  dayKey: Intl.DateTimeFormat;
  wallClockKey: Intl.DateTimeFormat;
};

const formatterCache = new Map<string, ZoneFormatters>();

function makeFormatters(tz: string): ZoneFormatters {
  return {
    weekdayShort: new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: "short",
    }),
    monthShort: new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      month: "short",
    }),
    dayNum: new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      day: "numeric",
    }),
    time: new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }),
    timeCompact: new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit",
    }),
    dayShort: new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      weekday: "short",
      month: "short",
      day: "numeric",
    }),
    dayKey: new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }),
    wallClockKey: new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  };
}

function ordinalSuffix(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return "th";
  switch (n % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function formatDayOrdinal(date: Date, fmt: ZoneFormatters): string {
  const weekday = fmt.weekdayShort.format(date);
  const month = fmt.monthShort.format(date);
  const dayNum = parseInt(fmt.dayNum.format(date), 10);
  return `${weekday} ${month} ${dayNum}${ordinalSuffix(dayNum)}`;
}

function formatTimeShort(
  date: Date,
  fmt: ZoneFormatters,
  withTz: boolean,
): string {
  const parts = (withTz ? fmt.time : fmt.timeCompact).formatToParts(date);
  const hour = parts.find((p) => p.type === "hour")?.value ?? "";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "";
  const dayPeriod =
    parts.find((p) => p.type === "dayPeriod")?.value.toLowerCase() ?? "";
  const tz = withTz
    ? parts.find((p) => p.type === "timeZoneName")?.value
    : null;
  const head = `${hour}:${minute}${dayPeriod}`;
  return tz ? `${head} ${tz}` : head;
}

export function formattersFor(tz: string): ZoneFormatters {
  const cached = formatterCache.get(tz);
  if (cached) return cached;
  let resolved: ZoneFormatters;
  let resolvedTz = tz;
  try {
    resolved = makeFormatters(tz);
  } catch {
    // Browser doesn't recognise this IANA zone. Fall back to the default.
    resolvedTz = DEFAULT_EVENT_TIMEZONE;
    resolved = formatterCache.get(resolvedTz) ?? makeFormatters(resolvedTz);
  }
  formatterCache.set(tz, resolved);
  if (resolvedTz !== tz) formatterCache.set(resolvedTz, resolved);
  return resolved;
}

function sameWallClock(iso: string, a: string, b: string): boolean {
  if (a === b) return true;
  try {
    const date = new Date(iso);
    return (
      formattersFor(a).wallClockKey.format(date) ===
      formattersFor(b).wallClockKey.format(date)
    );
  } catch {
    return false;
  }
}

export function formatEventDate(event: EmbedEvent): string {
  const eventTz = safeTz(event.timezone);
  const fmt = formattersFor(eventTz);

  const startDate = new Date(event.starts_at);
  const endDate = new Date(event.ends_at);
  const sameDay = fmt.dayKey.format(startDate) === fmt.dayKey.format(endDate);

  const showTz = !sameWallClock(event.starts_at, eventTz, viewerTimeZone());
  const startTime = formatTimeShort(startDate, fmt, false);
  const endTime = formatTimeShort(endDate, fmt, showTz);

  if (sameDay) {
    return `${formatDayOrdinal(startDate, fmt)} ${startTime} - ${endTime}`;
  }
  return `${formatDayOrdinal(startDate, fmt)} ${startTime} - ${formatDayOrdinal(endDate, fmt)} ${endTime}`;
}

export function eventSpansMultipleDays(event: EmbedEvent): boolean {
  const fmt = formattersFor(safeTz(event.timezone)).dayKey;
  return (
    fmt.format(new Date(event.starts_at)) !==
    fmt.format(new Date(event.ends_at))
  );
}

export function formatEventDateRangeShort(event: EmbedEvent): string {
  const fmt = formattersFor(safeTz(event.timezone));
  const startDate = new Date(event.starts_at);
  const endDate = new Date(event.ends_at);
  const startMonth = fmt.monthShort.format(startDate);
  const endMonth = fmt.monthShort.format(endDate);
  const startDay = parseInt(fmt.dayNum.format(startDate), 10);
  const endDay = parseInt(fmt.dayNum.format(endDate), 10);
  const startOrd = `${startDay}${ordinalSuffix(startDay)}`;
  const endOrd = `${endDay}${ordinalSuffix(endDay)}`;
  if (startMonth === endMonth) {
    return `${startMonth} ${startOrd}-${endOrd}`;
  }
  return `${startMonth} ${startOrd}-${endMonth} ${endOrd}`;
}

export function formatEventTimeRange(event: EmbedEvent): string {
  const startDate = new Date(event.starts_at);
  const endDate = new Date(event.ends_at);
  const fmt = formattersFor(safeTz(event.timezone)).timeCompact;
  return `${fmt.format(startDate)}–${fmt.format(endDate)}`;
}

export function formatEventDayAndTime(event: EmbedEvent): string {
  const startDate = new Date(event.starts_at);
  const endDate = new Date(event.ends_at);
  const fmts = formattersFor(safeTz(event.timezone));
  const day = fmts.dayShort.format(startDate);
  const start = fmts.timeCompact.format(startDate);
  const end = fmts.timeCompact.format(endDate);
  return `${day} · ${start}–${end}`;
}
